import { AnalysisReferenceComponent, analysisAddressLabel } from '@shared/components/analysis-reference.component';
import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  TemplateRef,
  input,
  output,
  computed,
  inject,
  effect,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideChartLine,
  lucideCalendar,
  lucideEllipsis,
  lucidePlay,
  lucideCircleStop,
  lucideTrash,
  lucideEye,
  lucideMapPin,
  lucideCircleCheck,
  lucideCircleDashed,
  lucideCircleX,
  lucideLoaderCircle,
  lucideArchive,
  lucideArchiveRestore,
  lucideGlobe,
  lucideEyeOff,
  lucideDownload,
  lucideCopy,
} from '@ng-icons/lucide';
import { HlmBadgeDirective } from '@spartan-ng/helm/badge';
import {
  Analysis,
  BusinessStatus,
  BUSINESS_STATUS_OPTIONS,
  ANALYSIS_TYPE_OPTIONS,
  BUSINESS_STATUS_METADATA,
} from '@shared/models/analysis.model';
import {
  DataTableComponent,
  ColumnDef,
  TableAction,
  TableToolbarAction,
} from '@shared/components/data-table/data-table';
import { CreateAnalysisDialog } from '@shared/components/create-analysis-dialog/create-analysis-dialog';
import { DownloadDialog } from '@shared/components/download-dialog/download-dialog';
import { AnalysisStatusSelector } from '@shared/components/analysis-status-selector/analysis-status-selector';
import { AnalysisSourceBadge } from '@shared/components/analysis-source-badge/analysis-source-badge';
import { AnalysisErrorDetailComponent } from '@shared/components/analysis-error-detail/analysis-error-detail';
import {
  getExecutionState,
  getExecutionStage,
  getExecutionLabel,
  isExecutionRunning,
  getExecutionIndicator,
  getTechnicalStateLabel,
  getTechnicalStageLabel,
} from '@shared/models/analysis-status.util';
import { AnalysisStore } from '@features/campaigns/store/analysis-store';
import { ToastService } from '@shared/services/toast.service';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';

const PIPELINE_OPTIONS = [
  { value: 'draft', label: 'Entwurf', badgeClass: 'bg-muted text-muted-foreground border-border' },
  { value: 'queued', label: 'Warteschlange', badgeClass: 'bg-accent text-accent-foreground border-primary/20' },
  { value: 'processing', label: 'Läuft', badgeClass: 'bg-warning-soft text-warning border-warning' },
  { value: 'completed', label: 'Abgeschlossen', badgeClass: 'bg-success-soft text-success border-success' },
  { value: 'failed', label: 'Fehler', badgeClass: 'bg-danger-soft text-danger border-danger' },
];

const STAGE_OPTIONS = [
  { value: 'idle', label: 'Bereit', badgeClass: 'bg-muted text-muted-foreground border-border' },
  { value: 'geocut', label: 'Geodaten', badgeClass: 'bg-accent text-accent-foreground border-primary/20' },
  { value: 'analyzing', label: 'Analyse', badgeClass: 'bg-accent text-accent-foreground border-primary/20' },
  { value: 'planning', label: 'Planung', badgeClass: 'bg-secondary text-muted-foreground border-border' },
  { value: 'complete', label: 'Fertig', badgeClass: 'bg-success-soft text-success border-success' },
];

const ARCHIVED_OPTIONS = [
  { value: 'active', label: 'Aktiv' },
  { value: 'archived', label: 'Archiviert' },
];

/** Every column the analysis table can render, in canonical display order. */
export type AnalysisColumnKey =
  | 'analysis'
  | 'type'
  | 'address'
  | 'consumption'
  | 'status'
  | 'source'
  | 'createdAt'
  /** Execution state and pipeline stage, merged into one badge. */
  | 'execution';

/**
 * Column visibility contract — every call site must state every column explicitly,
 * so a template alone tells you which columns render. Omitting a key fails to compile.
 */
export type AnalysisColumnVisibility = Record<AnalysisColumnKey, boolean>;

@Component({
  selector: 'app-analysis-table',
  standalone: true,
  imports: [
    AnalysisReferenceComponent,
    CommonModule,
    NgIconComponent,
    HlmBadgeDirective,
    DataTableComponent,
    CreateAnalysisDialog,
    DownloadDialog,
    AnalysisStatusSelector,
    AnalysisSourceBadge,
    AnalysisErrorDetailComponent,
  ],
  providers: [
    provideIcons({
      lucideChartLine,
      lucideCalendar,
      lucideEllipsis,
      lucidePlay,
      lucideCircleStop,
      lucideTrash,
      lucideEye,
      lucideMapPin,
      lucideCircleCheck,
      lucideCircleDashed,
      lucideCircleX,
      lucideLoaderCircle,
      lucideArchive,
      lucideArchiveRestore,
      lucideGlobe,
      lucideEyeOff,
      lucideDownload,
      lucideCopy,
    }),
  ],
  templateUrl: './analysis-table.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisTable {
  private readonly router = inject(Router);
  private readonly analysisStore = inject(AnalysisStore);
  private readonly toastService = inject(ToastService);
  private readonly confirmationDialog = inject(ConfirmationDialogService);
  readonly currentOrgStore = inject(CurrentOrganizationStore);

  // ─── Inputs ──────────────────────────────────────────────────────────────────

  isLoading = input<boolean>(false);
  organizationId = input.required<string>();
  campaignId = input<string | undefined>(undefined);
  currentPage = input<number>(1);
  totalPages = input<number>(0);
  /** When true, enables checkbox bulk-selection via the DataTable's built-in mechanism */
  selectable = input<boolean>(false);
  /** Total analysis count across all server-side pages — powers the "select all X" banner. */
  totalItems = input<number>(0);
  /** True while the parent fetches all rows for "select all across pages" — drives the banner spinner. */
  isSelectingAll = input<boolean>(false);
  /** When true, show only analyses that belong to the current execution (updatedAt >= executionSince). */
  onlyExecution = input<boolean>(false);
  /** ISO start time of the current run — rows updated at/after it belong to that run. */
  executionSince = input<string | null>(null);
  /** When set, restrict the table to these analysis IDs (matched on `id`) — used by the execution-history detail. */
  restrictToIds = input<Set<string> | null>(null);
  /** What can be done with the selected rows, shown in the table's selection strip. */
  selectionActions = input<TemplateRef<unknown> | null>(null);
  /** Show the "Neue Analyse" add button. Off for read-only contexts like execution history. */
  showAddButton = input<boolean>(true);
  toolbarActions = input<TableToolbarAction[]>([]);
  /**
   * The single authority over which columns render. Every key is required, so a call
   * site's template fully describes its table. Column *order* is fixed by the component.
   */
  columnVisibility = input.required<AnalysisColumnVisibility>();
  /** Columns a page adds right after the first one, e.g. a campaign's stage. */
  extraColumns = input<ColumnDef<Analysis>[]>([]);
  /** What a click on a row opens; the analysis page when not set. */
  rowOpen = input<((analysis: Analysis) => void) | null>(null);

  // ─── Outputs ─────────────────────────────────────────────────────────────────

  /** Emits the selected Analysis objects — use id/analysisId from them */
  selectionChange = output<Set<string>>();
  selectionChanged = output<string[]>();
  /** Emits when the user clicks "select all across all pages" in the DataTable banner. */
  requestSelectAll = output<void>();
  /** Emits when a bulk action (publish/unpublish) is triggered from the row context menu. */
  quickAction = output<{ type: 'publish' | 'unpublish'; analyses: Analysis[] }>();
  analysisCreated = output<Analysis>();
  dataChanged = output<void>();
  pageChange = output<number>();
  /**
   * Emits the Hub IDs of every row currently visible in the table — every active
   * filter, the search term, and the entry limit all already applied — or `null`
   * when nothing narrows the set (the "filtered" target then means the whole
   * campaign, resolved server-side instead of listing every ID).
   * Used by ExecutionPanel for "execute on filtered" / bulk template & media selection.
   */
  filterChanged = output<string[] | null>();

  // ─── ViewChild (signal-based) ─────────────────────────────────────────────────

  readonly dataTableRef = viewChild<DataTableComponent<Analysis>>(DataTableComponent);
  readonly downloadDialogRef = viewChild(DownloadDialog);

  constructor() {
    effect(() => {
      const dt = this.dataTableRef();
      if (!dt) return;
      if (!dt.hasActiveFilters()) {
        this.filterChanged.emit(null);
        return;
      }
      const ids = dt.limitedData()
        .map((a) => a.id)
        .filter((id): id is string => !!id);
      this.filterChanged.emit(ids);
    });
  }

  // ─── Data (from store) ────────────────────────────────────────────────────────

  readonly analyses = this.analysisStore.analyses;

  readonly tableData = computed(() => {
    let all = this.analyses();
    const restrict = this.restrictToIds();
    if (restrict) {
      all = all.filter((a) => !!a.id && restrict.has(a.id));
    }
    const since = this.executionSince();
    if (this.onlyExecution() && since) {
      const sinceMs = new Date(since).getTime();
      all = all.filter((a) => {
        const u = (a as any).updatedAt;
        return u && new Date(u).getTime() >= sinceMs;
      });
    }
    const active: Analysis[] = [];
    const archived: Analysis[] = [];
    all.forEach((a) => (this.isArchived(a) ? archived : active).push(a));
    return [...active, ...archived];
  });

  // ─── Column templates ─────────────────────────────────────────────────────────

  @ViewChild('createDialog') createDialog!: CreateAnalysisDialog;
  @ViewChild('analysisNameTpl', { static: true }) analysisNameTpl!: TemplateRef<any>;
  @ViewChild('typeTpl', { static: true }) typeTpl!: TemplateRef<any>;
  @ViewChild('addressTpl', { static: true }) addressTpl!: TemplateRef<any>;
  @ViewChild('consumptionTpl', { static: true }) consumptionTpl!: TemplateRef<any>;
  @ViewChild('statusTpl', { static: true }) statusTpl!: TemplateRef<any>;
  @ViewChild('dateTpl', { static: true }) dateTpl!: TemplateRef<any>;
  @ViewChild('sourceTpl', { static: true }) sourceTpl!: TemplateRef<any>;
  @ViewChild('executionTpl', { static: true }) executionTpl!: TemplateRef<any>;

  /** Canonical column order — identical in every usage; only visibility differs. */
  readonly columns = computed<ColumnDef<Analysis>[]>(() => {
    const visibility = this.columnVisibility();
    const candidates: Array<{ key: AnalysisColumnKey; col: ColumnDef<Analysis> }> = [
      {
        key: 'analysis',
        col: {
          header: 'Analyse',
          template: this.analysisNameTpl,
          searchValue: (a) => `${this.formatAddress(a)} ${a.analysisId}`,
          sortValue: (a) => this.formatAddress(a),
          class: 'min-w-[180px]',
        },
      },
      {
        key: 'address',
        col: {
          header: 'Adresse',
          template: this.addressTpl,
          searchValue: (a) => this.formatAddress(a),
          sortValue: (a) => this.formatAddress(a),
          class: 'min-w-[160px]',
        },
      },
      {
        key: 'status',
        col: {
          header: 'Status',
          template: this.statusTpl,
          sortValue: (a) => a.businessStatus ?? null,
          class: 'w-[90px]',
          filter: {
            type: 'multiselect',
            label: 'Status',
            getValue: (a) => a.businessStatus ?? null,
            options: BUSINESS_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
          },
        },
      },
      {
        key: 'type',
        col: {
          header: 'Typ',
          template: this.typeTpl,
          sortValue: (a) => a.analysisType ?? null,
          class: 'w-[90px]',
          filter: {
            type: 'multiselect',
            label: 'Typ',
            getValue: (a) => a.analysisType ?? null,
            options: [
              ...ANALYSIS_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
              { value: null, label: 'Nicht gesetzt' },
            ],
          },
        },
      },
      {
        key: 'consumption',
        col: {
          header: 'Verbrauch',
          template: this.consumptionTpl,
          sortValue: (a) => a.powerConsumption,
          class: 'w-[140px] text-right',
          filter: {
            type: 'range',
            label: 'Verbrauch',
            getValue: (a) => a.powerConsumption,
            unit: 'kWh',
          },
        },
      },
      {
        key: 'source',
        col: {
          header: 'Quelle',
          template: this.sourceTpl,
          searchValue: (a) => a.source ?? (a.campaignId ? 'campaign' : 'hub'),
          sortValue: (a) => a.source ?? (a.campaignId ? 'campaign' : 'hub'),
          class: 'w-[70px]',
          filter: {
            type: 'multiselect',
            label: 'Quelle',
            getValue: (a) => a.source ?? (a.campaignId ? 'campaign' : 'hub'),
            options: [
              { value: 'campaign', label: 'Kampagne' },
              { value: 'hub', label: 'Hub' },
              { value: 'plugin', label: 'Plugin' },
              { value: 'api', label: 'API' },
            ],
          },
        },
      },
      {
        key: 'createdAt',
        col: {
          header: 'Erstellt',
          template: this.dateTpl,
          sortValue: (a) => a.createdAt,
          class: 'w-[100px]',
          filter: { type: 'daterange', label: 'Erstellt', getValue: (a) => a.createdAt },
        },
      },
      {
        // State and stage in one badge — both filters stay, keyed by their own labels.
        key: 'execution',
        col: {
          header: 'Ausführung',
          template: this.executionTpl,
          sortValue: (a) => this.executionState(a),
          class: 'w-[150px]',
          filter: {
            type: 'multiselect',
            label: 'Ausführung',
            getValue: (a) => this.executionState(a),
            options: PIPELINE_OPTIONS,
          },
          filters: [
            {
              type: 'multiselect',
              label: 'Phase',
              getValue: (a) => this.executionStage(a),
              options: STAGE_OPTIONS,
            },
          ],
        },
      },
    ];

    const visible = candidates.filter((c) => visibility[c.key]).map((c) => c.col);
    return [...visible.slice(0, 1), ...this.extraColumns(), ...visible.slice(1)];
  });

  /** Resolves selected rows to full Analysis objects across all loaded pages — mirrors AddressTable's selection pattern. */
  readonly selectedAnalysisObjects = computed<Analysis[]>(
    () => this.dataTableRef()?.selectedObjects() ?? []
  );

  readonly actions = computed<TableAction<Analysis>[]>(() => {
    const selectedCount = this.selectedAnalysisObjects().length;
    const inCampaign = !!this.campaignId();
    const canUpdate = this.currentOrgStore.can('analysis', 'update');
    const canDelete = this.currentOrgStore.can('analysis', 'delete');
    const canEditCampaign = this.currentOrgStore.can('campaign', 'update');
    const canExportCampaign = this.currentOrgStore.can('campaign', 'export');
    const withCount = (label: string) =>
      selectedCount > 1 ? `${label} (${selectedCount})` : label;
    const targets = (analysis: Analysis): Analysis[] => {
      const selected = this.selectedAnalysisObjects();
      return selected.length > 0 ? selected : [analysis];
    };

    return [
      { label: 'Anzeigen', icon: 'lucideEye', action: (a) => this.onAction('view', a), visible: () => true },
      { label: 'Abbrechen', icon: 'lucideCircleStop', action: (a) => this.onAction('cancel', a), visible: (a) => canUpdate && this.canCancel(a) },
      {
        label: (_a: Analysis) => withCount('Veröffentlichen'),
        icon: 'lucideGlobe',
        dividerBefore: true,
        visible: () => inCampaign && canEditCampaign,
        action: (a) => this.quickAction.emit({ type: 'publish', analyses: targets(a) }),
      },
      {
        label: (_a: Analysis) => withCount('Nicht öffentlich'),
        icon: 'lucideEyeOff',
        visible: () => inCampaign && canEditCampaign,
        action: (a) => this.quickAction.emit({ type: 'unpublish', analyses: targets(a) }),
      },
      {
        label: (_a: Analysis) => withCount('Herunterladen'),
        icon: 'lucideDownload',
        visible: () => inCampaign && canExportCampaign,
        action: (a) => this.openDownloadDialog(targets(a)),
      },
      { label: 'Archivieren', icon: 'lucideArchive', action: (a) => this.onAction('archive', a), visible: (a) => canUpdate && !this.isArchived(a), dividerBefore: true },
      { label: 'Wiederherstellen', icon: 'lucideArchiveRestore', action: (a) => this.onAction('unarchive', a), visible: (a) => canUpdate && this.isArchived(a), dividerBefore: true },
      { label: 'Löschen', icon: 'lucideTrash', type: 'destructive', action: (a) => this.onAction('delete', a), visible: () => canDelete, dividerBefore: true },
    ];
  });

  /** Identity function — enables ID-based selection that survives server-side page changes. */
  readonly getRowId = (a: Analysis) => a.analysisId;

  // ─── Selection bridge ─────────────────────────────────────────────────────────

  onSelectionIdsChanged(ids: (string | number)[]): void {
    const stringIds = ids.map(String);
    this.selectionChange.emit(new Set(stringIds));
    this.selectionChanged.emit(stringIds);
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  onAddNew(): void { this.createDialog?.open(); }

  onRowClick(analysis: Analysis): void {
    const open = this.rowOpen();
    if (open) open(analysis);
    else void this.onAction('view', analysis);
  }

  openDownloadDialog(analyses: Analysis[]): void {
    const ids = analyses.map((a) => (a as any).id ?? a.analysisId);
    this.downloadDialogRef()?.open(ids);
  }
  onAnalysisCreated(analysis: Analysis): void { this.analysisCreated.emit(analysis); }

  async onAction(type: 'view' | 'delete' | 'cancel' | 'create' | 'archive' | 'unarchive', analysis: Analysis): Promise<void> {
    switch (type) {
      case 'view': await this.handleView(analysis); break;
      case 'cancel': await this.handleCancel(analysis); break;
      case 'archive': await this.handleArchive(analysis); break;
      case 'unarchive': await this.handleUnarchive(analysis); break;
      case 'delete': await this.handleDelete(analysis); break;
    }
  }

  private async handleView(analysis: Analysis): Promise<void> {
    const org = this.currentOrgStore.organization();
    if (!org) { this.toastService.error('Organisation nicht verfügbar'); return; }
    await this.router.navigate(['/organizations', org.slug, 'analyses', analysis.analysisId]);
  }

  private async handleCancel(analysis: Analysis): Promise<void> {
    if (!this.canCancel(analysis)) return;
    const ok = await this.confirmationDialog.confirm({
      title: 'Analyse abbrechen', description: 'Sind Sie sicher?', type: 'warning',
      itemInfo: { label: `Analyse #${analysis.analysisId.slice(0, 8)}` },
      warningMessage: 'Fortschritt geht verloren.', confirmText: 'Abbrechen',
      onError: (e) => this.toastService.error('Fehler', e),
    });
    if (!ok) return;
    const r = await this.analysisStore.cancelAnalysis(this.organizationId(), analysis.analysisId);
    r.success ? this.toastService.success('Analyse abgebrochen') : this.toastService.error(r.error ?? 'Fehler');
    if (r.success) this.dataChanged.emit();
  }

  private async handleDelete(analysis: Analysis): Promise<void> {
    const ok = await this.confirmationDialog.confirm({
      title: 'Analyse löschen', description: 'Unwiderruflich löschen?', type: 'danger',
      itemInfo: { label: `Analyse #${analysis.analysisId.slice(0, 8)}` },
      warningMessage: 'Kann nicht rückgängig gemacht werden.', confirmText: 'Löschen',
      onError: (e) => this.toastService.error('Fehler', e),
    });
    if (!ok) return;
    const r = await this.analysisStore.deleteAnalysis(this.organizationId(), analysis.analysisId);
    r.success ? this.toastService.success('Analyse gelöscht') : this.toastService.error(r.error ?? 'Fehler');
    if (r.success) this.dataChanged.emit();
  }

  private async handleArchive(analysis: Analysis): Promise<void> {
    const ok = await this.confirmationDialog.confirm({
      title: 'Analyse archivieren', description: 'Archivieren?', type: 'warning',
      itemInfo: { label: `Analyse #${analysis.analysisId.slice(0, 8)}` },
      warningMessage: 'Kann wiederhergestellt werden.', confirmText: 'Archivieren',
      onError: (e) => this.toastService.error('Fehler', e),
    });
    if (!ok) return;
    const r = await this.analysisStore.archiveAnalysis(this.organizationId(), analysis.analysisId);
    r.success ? this.toastService.success('Archiviert') : this.toastService.error(r.error ?? 'Fehler');
    if (r.success) this.dataChanged.emit();
  }

  private async handleUnarchive(analysis: Analysis): Promise<void> {
    const r = await this.analysisStore.unarchiveAnalysis(this.organizationId(), analysis.analysisId);
    r.success ? this.toastService.success('Wiederhergestellt') : this.toastService.error(r.error ?? 'Fehler');
    if (r.success) this.dataChanged.emit();
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  isArchived(a: Analysis): boolean { return !!a.archivedAt; }

  getRowClass(a: Analysis): string {
    return this.isArchived(a) ? 'opacity-50 bg-background hover:bg-muted' : '';
  }

  canCancel(a: Analysis): boolean {
    const state = a.technicalStatus?.state;
    return !state || state === 'draft';
  }

  /** Copies the full analysis ID; stops propagation so the row click does not navigate. */

  // Execution state/stage display — shared with other analysis lists via analysis-status.util.
  executionState = getExecutionState;
  executionStage = getExecutionStage;
  executionLabel = getExecutionLabel;
  isExecutionRunning = isExecutionRunning;
  executionIndicator = getExecutionIndicator;
  getTechnicalStateLabel = getTechnicalStateLabel;
  getTechnicalStageLabel = getTechnicalStageLabel;

  formatAddress(a: Analysis): string { return analysisAddressLabel(a.address); }

  async onStatusChange(a: Analysis, status: BusinessStatus): Promise<void> {
    if (a.businessStatus === status) return;
    const r = await this.analysisStore.updateStatus(this.organizationId(), a.analysisId, status);
    r.success ? this.toastService.success('Status aktualisiert') : this.toastService.error(r.error ?? 'Fehler');
  }

}
